import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {FetchStatus} from '../common/FetchStatus.enum';
import {requestContext} from '../common/requestContext';
import {AppSetting, UserFragment} from '../graphql/generated/types';
import {USER_STATE_NAME} from './constants';
import {UserFetchStatusKey, UserState} from './UserState.interface';
import {updateProfileImage} from './updateProfileImage.thunk';

export const userSlice = createSlice({
  name: USER_STATE_NAME,
  initialState: () =>
    ({
      user: requestContext?.requestInfo?.user,
      appConfig: {
        appSetting: {
          tastesHome: requestContext?.requestInfo?.appConfig?.tastesHome,
          tastesGenre: requestContext?.requestInfo?.appConfig?.tastesGenre,
          tastesServiceLang:
            requestContext?.requestInfo?.appConfig?.tastesServiceLang,
        },
      },
      fetchStatus: {
        [UserFetchStatusKey.UpdateProfileImage]: FetchStatus.Idle,
        [UserFetchStatusKey.GetUserData]: FetchStatus.Success,
      },
      error: {
        message: undefined,
      },
    }) as UserState,
  reducers: {
    setUser: (state, action: PayloadAction<UserFragment>) => {
      state.user = action.payload;
    },
    setFetchStatus: (
      state,
      action: PayloadAction<{
        fetchStatusKey: UserFetchStatusKey;
        fetchStatus: FetchStatus;
      }>
    ) => {
      state.fetchStatus[action.payload.fetchStatusKey] =
        action.payload.fetchStatus;
    },
    setAge: (state, action: PayloadAction<number | undefined>) => {
      if (state.user) {
        state.user.age = action.payload;
        if (!action.payload) {
          state.user.isParentCertificated = false;
        }

        if (requestContext.requestInfo.user) {
          requestContext.requestInfo.user = {
            ...requestContext.requestInfo.user,
            age: action.payload,
          };
        }
      }
    },
    setIsParentSelfCertificated: (state, action: PayloadAction<boolean>) => {
      if (state.user) {
        state.user.isParentCertificated = action.payload;
      }
    },
    setAuthenticatedAt: (state, action: PayloadAction<string>) => {
      if (state.user) {
        state.user.authenticatedAt = action.payload;

        if (requestContext.requestInfo.user) {
          requestContext.requestInfo.user = {
            ...requestContext.requestInfo.user,
            authenticatedAt: action.payload,
          };
        }
      }
    },
    setAppSetting: (state, action: PayloadAction<Partial<AppSetting>>) => {
      state.appConfig = {
        ...state.appConfig,
        appSetting: {
          ...state.appConfig?.appSetting,
          ...action.payload,
        },
      };
    },
  },
  extraReducers: builder => {
    builder.addCase(updateProfileImage.fulfilled, (state, {payload}) => {
      const {profileImage} = payload;
      if (state.user) {
        state.user.thumbnail = profileImage;
      }
    });
    builder.addCase(updateProfileImage.rejected, (state, {payload}) => {
      state.fetchStatus.updateProfileImage = FetchStatus.Error;
      if (payload) {
        state.error.message = payload.message;
      }
    });
  },
});

export const {
  setUser,
  setFetchStatus,
  setAge,
  setIsParentSelfCertificated,
  setAuthenticatedAt,
  setAppSetting,
} = userSlice.actions;
